import React, { useMemo, useState } from 'react';

// components
import { FormButton } from '../ui/buttons/FormButton';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import {
  ModalContainer,
  CloseButtonContainer,
  ModalBody,
  ModalFooter,
  ModalFormContainer,
  ModalHeader,
  ModalHeaderInnerContainer,
  ModalHeaderMainText,
  ModalHeaderSecondaryText,
  ModalBoldText,
  ModalRegularText,
} from './modalComponents';
import ticketService from '../../ticketService';
import { PremiumInput } from '../ui/inputs/customInput';
import { Half, SectionContainer, SectionLine } from '@ontruck/fe-ui';
import t from './TicketUpdateTranslations';
import { ReactComponent as ImageIcon } from 'assets/icons/image.svg';
import AttachementLabel from '../ui/inputs/ui/AttachementLabel';
import AttachementItem from '../ui/inputs/ui/AttachementItem';
import commsLanguageDisplayText from 'modules/emergencies/constants/commsLanguageDisplayText';

export const TicketUpdatePickup = ({ ticket, type, ok, cancel }) => {
  const commsLanguage = ticket.shipper_data?.language || 'es';
  const initialPickupCity = ticket.order_data?.pickup?.city || '';
  const initialPickupCountry = ticketService.utils.mapCountryCodeToCountryName(
    ticket.order_data?.pickup?.country || ''
  );
  const loads = ticket.order_data.loads_data.loads;
  const initialLoads = loads.map((load) => `${load.number_of_items} ${load.load_type}`).join(', ');
  const [pickupCity, setPickupCity] = useState(initialPickupCity);
  const [pickupCountry, setPickupCountry] = useState(initialPickupCountry);
  const [load, setLoad] = useState(initialLoads);
  const [plate, setPlate] = useState('');

  const [showErrors, setShowErrors] = useState(false);

  const [rawFiles, setRawFiles] = useState([]);

  const handleFileChange = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).filter((file) => {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif'];
        if (!allowedExtensions.includes(fileExtension)) {
          return false;
        }
        return true;
      });
      setRawFiles([...rawFiles, ...newFiles]);
    }
  };
  const removeFileByIndex = (index) => {
    setRawFiles(rawFiles.filter((_, i) => i !== index));
  };

  const formattedTicketId = useMemo(
    () => ticketService.utils.getFormatedId(ticket.id),
    [ticket.id]
  );

  const hasPickupCityError = useMemo(() => pickupCity.trim().length === 0, [pickupCity]);

  const hasPickupCountryError = useMemo(() => pickupCountry.trim().length === 0, [pickupCountry]);

  const hasLoadError = useMemo(() => load.trim().length === 0, [load]);

  const hasPlateError = useMemo(() => plate.trim().length === 0, [plate]);

  const hasErrors = useMemo(
    () => hasPickupCityError || hasPickupCountryError || hasLoadError || hasPlateError,
    [hasLoadError, hasPickupCityError, hasPickupCountryError, hasPlateError]
  );

  const goBack = (e) => {
    e.preventDefault();
    cancel();
  };

  const baseUpdateData = {
    load_detail: null,
    plate_number: null,
    //
    pickup_address: null,
    pickup_city: null,
    pickup_country: null,
    //
    dropoff_address: null,
    dropoff_city: null,
    dropoff_country: null,
    //
    eta: null,
    //
    additional_information: null,
  };

  const onSubmit = () => {
    setShowErrors(true);
    if (!hasErrors) {
      const dto = {
        update_data: {
          ...baseUpdateData,
          pickup_city: pickupCity,
          pickup_country: pickupCountry,
          load_detail: load,
          plate_number: plate,
        },
        rawFiles,
        type,
      };
      ok(dto);
    }
  };

  return (
    <ModalContainer>
      <ModalBody>
        <ModalHeader>
          <ModalHeaderInnerContainer>
            <ModalHeaderSecondaryText>{`Ticket ID. ${formattedTicketId}`}</ModalHeaderSecondaryText>
            <ModalHeaderMainText>
              {t.sendMessage}: {t[type]}
            </ModalHeaderMainText>
          </ModalHeaderInnerContainer>
        </ModalHeader>
        <ModalFormContainer>
          {/* Pickup */}
          <SectionContainer>
            <ModalBoldText>{t.pickupSection}</ModalBoldText>
            <SectionLine>
              <Half>
                <PremiumInput
                  type="text"
                  label={t.city}
                  value={pickupCity}
                  onChange={(e) => setPickupCity(e.target.value)}
                  hasError={showErrors && hasPickupCityError}
                  isRequired={true}
                />
              </Half>
              <Half>
                <PremiumInput
                  type="text"
                  label={t.country}
                  value={pickupCountry}
                  onChange={(e) => setPickupCountry(e.target.value)}
                  hasError={showErrors && hasPickupCountryError}
                  isRequired={true}
                />
              </Half>
            </SectionLine>
          </SectionContainer>
          {/* Load & vehicle */}
          <SectionContainer>
            <SectionLine>
              <Half>
                <ModalBoldText>{t.loadSection}</ModalBoldText>
                <SectionLine>
                  <PremiumInput
                    type="text"
                    label={t.load}
                    value={load}
                    onChange={(e) => setLoad(e.target.value)}
                    hasError={showErrors && hasLoadError}
                    isRequired={true}
                  />
                </SectionLine>
              </Half>
              <Half>
                <ModalBoldText>{t.vehicleSection}</ModalBoldText>
                <SectionLine>
                  <PremiumInput
                    type="text"
                    label={t.plate}
                    value={plate}
                    onChange={(e) => setPlate(e.target.value)}
                    hasError={showErrors && hasPlateError}
                    isRequired={true}
                  />
                </SectionLine>
              </Half>
            </SectionLine>
          </SectionContainer>
          {/* files */}
          <SectionContainer>
            <SectionLine>
              <AttachementLabel htmlFor="file">
                <input
                  id="file"
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif"
                  onChange={handleFileChange}
                  multiple
                  style={{ display: 'none' }}
                />
                <ImageIcon />
                Upload images
              </AttachementLabel>
            </SectionLine>
            <SectionLine>
              {rawFiles.map((file, index) => (
                <AttachementItem
                  key={index}
                  name={file.name}
                  onClick={() => removeFileByIndex(index)}
                />
              ))}
            </SectionLine>
          </SectionContainer>
          {/* comms lang */}
          <ModalRegularText>
            Language defined for the customer comms.: {commsLanguageDisplayText[commsLanguage]}
          </ModalRegularText>
        </ModalFormContainer>
      </ModalBody>
      <ModalFooter>
        <FormButton variant="ghost" onClick={goBack}>
          {t.back}
        </FormButton>
        <FormButton isDisabled={showErrors && hasErrors} onClick={onSubmit}>
          {t.send}
        </FormButton>
      </ModalFooter>
      <CloseButtonContainer onClick={goBack}>
        <CloseIcon />
      </CloseButtonContainer>
    </ModalContainer>
  );
};

export default TicketUpdatePickup;
